import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, Package, IndianRupee, Clock, CheckCircle2, RefreshCw } from "lucide-react";
import { getAdminOrders, getAdminStats } from "@/lib/api";

const StatCard = ({ icon: Icon, label, value, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.06, duration: 0.5 }}
    className="glass rounded-2xl p-6"
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className="h-10 w-10 rounded-xl border border-gold/25 grid place-items-center mb-4">
      <Icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
    </div>
    <p className="text-xs uppercase tracking-[0.15em] text-vmuted">{label}</p>
    <p className="font-serif text-3xl text-white mt-1">{value}</p>
  </motion.div>
);

const StatusPill = ({ status }) => {
  const map = {
    paid: "bg-gold/15 text-gold",
    pending: "bg-white/10 text-white/70",
    failed: "bg-red-500/15 text-red-300",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs capitalize ${map[status] || map.pending}`}>
      {status}
    </span>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = typeof window !== "undefined" ? localStorage.getItem("vv_admin_token") : null;

  const load = async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([getAdminOrders(token), getAdminStats(token)]);
      setOrders(o);
      setStats(s);
    } catch (err) {
      localStorage.removeItem("vv_admin_token");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
  }, []);

  const logout = () => {
    localStorage.removeItem("vv_admin_token");
    navigate("/admin/login");
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <header className="glass sticky top-0 z-30 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <img src="/assets/veda_logo_mark.png" alt="Veda Vitals" className="h-7 w-auto" />
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="h-10 px-4 inline-flex items-center gap-2 rounded-full glass hover:border-gold/40 transition-colors text-sm"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="h-10 px-4 inline-flex items-center gap-2 rounded-full glass hover:border-gold/40 transition-colors text-sm"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <h1 className="font-serif text-3xl md:text-4xl mb-8">Orders Overview</h1>

        {loading ? (
          <div className="grid place-items-center py-24">
            <Loader2 className="h-8 w-8 text-gold animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-10">
              <StatCard i={0} icon={Package} label="Total Orders" value={stats?.total_orders ?? 0} />
              <StatCard i={1} icon={IndianRupee} label="Revenue" value={`₹${stats?.revenue ?? 0}`} />
              <StatCard i={2} icon={CheckCircle2} label="Paid" value={stats?.paid ?? 0} />
              <StatCard i={3} icon={Clock} label="Pending" value={stats?.pending ?? 0} />
            </div>

            <div className="glass rounded-2xl overflow-hidden" data-testid="orders-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-vmuted border-b border-white/10">
                      <th className="px-5 py-4 font-medium">Customer</th>
                      <th className="px-5 py-4 font-medium">Contact</th>
                      <th className="px-5 py-4 font-medium">Plan</th>
                      <th className="px-5 py-4 font-medium">Qty</th>
                      <th className="px-5 py-4 font-medium">Amount</th>
                      <th className="px-5 py-4 font-medium">Status</th>
                      <th className="px-5 py-4 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-5 py-16 text-center text-vmuted">
                          No orders yet.
                        </td>
                      </tr>
                    ) : (
                      orders.map((o, i) => (
                        <tr
                          key={o.id}
                          data-testid={`order-row-${i}`}
                          className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                        >
                          <td className="px-5 py-4">
                            <p className="text-white">{o.customer?.name}</p>
                            <p className="text-xs text-vmuted max-w-[220px] truncate">{o.customer?.address}</p>
                          </td>
                          <td className="px-5 py-4 text-vmuted">
                            <p>{o.customer?.email}</p>
                            <p className="text-xs">{o.customer?.phone}</p>
                          </td>
                          <td className="px-5 py-4 text-white/90">{o.plan_title}</td>
                          <td className="px-5 py-4 text-white/90">{o.quantity}</td>
                          <td className="px-5 py-4 font-serif text-gold text-lg">₹{o.amount}</td>
                          <td className="px-5 py-4"><StatusPill status={o.status} /></td>
                          <td className="px-5 py-4 text-vmuted text-xs">{fmt(o.created_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
